import ArticleCard from "@components/Blocks/Card/Article/ArticleCard";
import type { CreateLoadMoreHandlerProps } from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import { CreateLoadMoreHandler } from "@components/Modules/LoadMore/CreateLoadMoreHandler";
import LoadMore from "@components/Modules/LoadMore/LoadMore";
import useSetFirstRenderedArticleIds from "@components/Modules/LoadMore/SetFirstRenderedArticleIds";
import { SEASONAL_TAGS } from "@config/ndr2025";
import { CompanionAdsLb } from "@elements/Advertisement/variant/CompanionAdsLb";
import fetchBHApi from "@helper/fetchBHApi";
import { displayAfter } from "@helper/utils";
import type { ProcessedArticleData } from "@transformer/useOSResponse";
import {
  AdTargetingType,
  ArticleAdTargetKeys,
  PageAdTargetingTypeEnum,
} from "@typings/Ads.d";
import type { MoreArticlesRequestData } from "@typings/MoreArticlesApi";
import React, { useState } from "react";

const PAGE_SIZE = 5;
const INITIAL_ARTICLE_COUNT = 5;

type KeywordArticlesListProps = {
  articles: ProcessedArticleData[];
  adsSectionName: string;
  adsSlotTarget?: AdTargetingType<ArticleAdTargetKeys, string>[];
};

export default function SeasonalKeywordArticleList({
  articles,
  adsSectionName,
  adsSlotTarget,
}: KeywordArticlesListProps) {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [renderedArticleIds, setRenderedArticleIds] = useState(
    new Set<string>(),
  );
  const [loadMoreData, setLoadMoreData] = useState<ProcessedArticleData[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const showLoadMore: boolean = articles.length == INITIAL_ARTICLE_COUNT;

  /**
   * Sets a precaution for duplicate article ids when rendering additional articles
   */
  useSetFirstRenderedArticleIds(articles, setRenderedArticleIds);

  const handleLoadMore = async () => {
    const lastLoadedArticle =
      loadMoreData.length === 0 ? articles.at(-1) : loadMoreData.at(-1);
    const requestData: MoreArticlesRequestData = {
      filterArticlesBasedOn: SEASONAL_TAGS,
      sort: lastLoadedArticle?.sort,
    };

    const fetchMoreNdrTagsStoriesData = async (
      requestData: MoreArticlesRequestData,
    ): Promise<ProcessedArticleData[] | []> => {
      const queryParams = new URLSearchParams({
        tagAlias: requestData.filterArticlesBasedOn as string,
        sort: Array.isArray(requestData.sort) ? requestData.sort.join(",") : "",
        size: `${PAGE_SIZE}`,
      }).toString();
      const response: ProcessedArticleData[] = await fetchBHApi(
        `more-tags-stories?${queryParams}`,
        "GET",
      );

      return response;
    };

    const createLoadMoreHandlerProps: CreateLoadMoreHandlerProps = {
      requestData,
      fetchFunction: fetchMoreNdrTagsStoriesData,
      setHasMoreStories,
      setRenderedArticleIds,
      setLoadMoreData,
      setPageNumber,
      lastLoadedArticle,
      loadMoreData,
      renderedArticleIds,
      pageSize: PAGE_SIZE,
      pageNumber,
    };

    const response = await CreateLoadMoreHandler(createLoadMoreHandlerProps);
    return response;
  };

  return (
    <div className="flex flex-col gap-5">
      {[...articles, ...loadMoreData].map((context, index) => (
        <React.Fragment key={index}>
          {displayAfter(index, PAGE_SIZE) && (
            <div data-testid={index}>
              <CompanionAdsLb
                uniqueSectionName={adsSectionName}
                index={index / PAGE_SIZE}
                pageAdTargetType={PageAdTargetingTypeEnum.TAG}
                articleSlotTarget={adsSlotTarget}
              />
            </div>
          )}
          <ArticleCard
            {...context}
            forNewDesign={true}
            showSection={false}
            displayWidth={284}
            displayHeight={190}
            orientation="horizontal-summary"
            variant="2md"
          />
        </React.Fragment>
      ))}
      {showLoadMore ? (
        <LoadMore
          rootClassName="my-4"
          onLoadMore={handleLoadMore}
          hasMore={hasMoreStories}
          loadText="Lagi Cerita"
          virtualPVTitle="NDR2025"
          pageNumber={pageNumber}
        />
      ) : null}
    </div>
  );
}
